import slugify from 'slugify';
import { differenceInHours } from 'date-fns';
import {
  Conversation as APIConversation,
  ConversationBasic as APIConversationBasic,
  EditConversationResponse,
  Message as APIMessage,
  MessageMeta as APIMessageMeta,
  MessagePersona as APIMessagePersona,
  MessagePost as APIMessagePost,
} from '@sdk/messages/types/apiTypes';
import { isMessageMeta, isMessagePersona, isMessagePost } from '@sdk/messages/utils/guards';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import { PersonaCollection } from '@sdk/personaCollection/types/PersonaCollection';
import { sanitizeNestedBlockquotes } from '@sdk/components/richText/helpers/sanitize';

import { escapeAttachmentUrl } from '@web/common/utils/escapeAttachmentUrl';
import { Conversation, ConversationBasic } from '@web/conversations/types/Conversation';
import {
  MessagePost,
  Message,
  MessageMeta,
  MessagePersona,
} from '@web/conversations/types/Message';
import { MessageType } from '@web/conversations/types/MessageType';

import { getConversationInterlocutor } from './getConversationInterlocutor';
import { getMessagePersona, getMessageUser } from './getMessagePersona';

const plainObjectIdToArray = <T extends IDObject>(objects: T[]): ID[] =>
  objects.map(({ id }) => id);

type MapperProps<T> = {
  currentUser?: AuthUserType;
  message: T;
  personaCollection: PersonaCollection;
};

export const mapAPIMessagePostToUI = ({
  currentUser,
  message,
  personaCollection,
}: MapperProps<APIMessagePost>): MessagePost => {
  const postedAt = new Date(message.timestamp);
  const author = getMessageUser(message.author, personaCollection, currentUser);
  const elapsed12HoursAfterPosted = differenceInHours(Date.now(), postedAt) >= 12;

  return {
    attachments: escapeAttachmentUrl(message.attachments),
    author,
    canEdit: !message.deleted && author.isMe && !elapsed12HoursAfterPosted,
    // @TODO probably needs to respect conversation private and restricted fields
    canDelete:
      !message.deleted &&
      !elapsed12HoursAfterPosted &&
      (author.isMe || currentUser.isAdmin || (!author.isAdmin && currentUser.isManager)),
    content: sanitizeNestedBlockquotes(message?.content.trim()),
    conversationId: message.conversationId,
    editedAt: message.lastmodified ? new Date(message.lastmodified) : undefined,
    hasFailedToSubmit: false,
    id: message.id,
    isDeleted: message.deleted ?? false,
    isEdited: !!message.lastmodified,
    parentMessage: message.parentMessage
      ? mapAPIMessagePostToUI({ currentUser, message: message.parentMessage, personaCollection })
      : null,
    postedAt,
    reactions: message.emojiReactions,
    type: MessageType.MESSAGE,
  };
};

export const mapAPIMessageMetaToUI = ({
  currentUser,
  message,
  personaCollection,
}: MapperProps<APIMessageMeta>): MessageMeta => {
  const author = getMessageUser(message.author, personaCollection, currentUser);

  const type = MessageType[message.type.toUpperCase()] as
    | MessageType.CREATION
    | MessageType.RENAMING;

  return {
    author,
    conversationId: message.conversationId,
    id: message.id,
    name: message.name,
    postedAt: new Date(message.timestamp),
    type,
  };
};

export const mapAPIMessagePersonaToUI = ({
  currentUser,
  message,
  personaCollection,
}: MapperProps<APIMessagePersona>): MessagePersona => {
  const author = getMessageUser(message.author, personaCollection, currentUser);

  let personaIds = [...message.personaIds];
  const includesCurrentuser = personaIds.includes(currentUser.id);
  // @INFO current user has to be the first persona
  if (includesCurrentuser) {
    personaIds = [currentUser.id].concat(personaIds.filter((id) => id !== currentUser.id));
  }

  const type = MessageType[message.type.toUpperCase()] as
    | MessageType.ADDITION
    | MessageType.REMOVAL;

  const personaCount = personaIds.length;
  return {
    author,
    conversationId: message.conversationId,
    firstPersona: getMessagePersona({ id: personaIds[0] }, personaCollection, currentUser),
    id: message.id,
    personaCount,
    postedAt: new Date(message.timestamp),
    secondPersona:
      personaCount > 1
        ? getMessagePersona({ id: personaIds[1] }, personaCollection, currentUser)
        : undefined,
    type,
  };
};

export const mapAPIMessageStrategy = ({
  currentUser,
  message,
  personaCollection,
}: MapperProps<APIMessage>): Message => {
  if (isMessagePost(message))
    return mapAPIMessagePostToUI({ currentUser, message, personaCollection });
  if (isMessageMeta(message))
    return mapAPIMessageMetaToUI({ currentUser, message, personaCollection });
  if (isMessagePersona(message))
    return mapAPIMessagePersonaToUI({ currentUser, message, personaCollection });
};

export const mapBulkAPIMessages = ({
  currentUser,
  message: messages,
  personaCollection,
}: MapperProps<APIMessage[]>): Message[] =>
  messages.map((message) => mapAPIMessageStrategy({ currentUser, message, personaCollection }));

export const mapAPIConversations = (conversations: APIConversationBasic[]) =>
  conversations.map((conversation) => mapAPIConversationBasicToUI(conversation));

export const mapAPIConversationBasicToUI = (
  conversation: APIConversationBasic
): ConversationBasic => ({
  id: conversation.id,
  isArchived: conversation.archived ?? false,
  isPinned: conversation.pinned,
  isPrivate: conversation.private,
  isSilenced: conversation.silenced,
  name: conversation.name,
  newestMessageId: conversation.lastwriteId,
  newestMessagePostedAt: new Date(conversation.lastwriteAt),
  newestUnsilencedMessagePostedAt: new Date(
    conversation.lastwriteAtWhenSilenced
      ? conversation.lastwriteAtWhenSilenced
      : conversation.lastwriteAt
  ),
  unreadMessagesCount: conversation.unreadItems ?? 0,
  slug: slugify(conversation.name, { strict: true }),
});

export const mapAPIConversationToUI = (
  conversation: APIConversation,
  personaCollection: PersonaCollection,
  currentUser: AuthUserType
): Conversation => {
  const mappedBasicConversation = mapAPIConversationBasicToUI(conversation);

  return {
    ...mappedBasicConversation,
    creatorId: conversation.created_by,
    firstUnreadMessageId: conversation.firstUnreadMessageId,
    interlocutor: getConversationInterlocutor(conversation, personaCollection, currentUser),
    isDisabled: conversation.disabled,
    memberIds: plainObjectIdToArray(conversation.personas),
    restricted: conversation.restricted,
    restrictedToPersonas: conversation.restrictedToPersonas || [],
  };
};

export const mapNewConversation = (
  conversation: Omit<APIConversation, 'firstUnreadMessageId' | 'messages'>
): ConversationBasic => mapAPIConversationBasicToUI(conversation);

// @TODO consider defining extra type, that precisely says what fields can be edited
export const mapEditedConversation = (
  conversation: EditConversationResponse
): Partial<Conversation> => ({
  firstUnreadMessageId: conversation.firstUnreadMessageId,
  id: conversation.id,
  memberIds: plainObjectIdToArray(conversation.personas),
  name: conversation.name,
  newestMessageId: conversation.lastwriteId,
  newestMessagePostedAt: new Date(conversation.lastwriteAt),
  newestUnsilencedMessagePostedAt: new Date(
    conversation.lastwriteAtWhenSilenced
      ? conversation.lastwriteAtWhenSilenced
      : conversation.lastwriteAt
  ),
  restricted: conversation.restricted,
  restrictedToPersonas: conversation.restrictedToPersonas || [],
  unreadMessagesCount: conversation.unreadItems ?? 0,
  slug: slugify(conversation.name, { strict: true }),
});
